import { CustodianService } from 'src/app/services/custodian.service';
import { DocService } from 'src/app/services/doc.service';
import { FilterField } from 'src/app/components/dialogs/select-filter-new/models/filter-field';
import { ReviewSetService } from 'src/app/services/review-set.service';
import { SelectFilterComponent } from '../../select-filter/select-filter.component';
import { FilterFieldGuids } from 'src/app/components/dialogs/select-filter-new/models/filter-field-guids';
import { BuiltInFieldId } from 'src/app/models/built-in-field-id.enum';
import { Enumeration } from 'src/app/models/enumeration.model';
import { AutoRedactionService } from '../../../../services/auto-redaction-service/auto-redaction.service';
import { RunStatus } from 'src/app/models/run-status.enum';
import { UserManager } from '../../../../managers/user.manager';
import { UploadBatchesSdk } from "../../../../sdks/upload-batches.sdk";
import { ProductionJobSdk } from 'src/app/sdks/production-job.sdk';
import { BriefcaseJobSdk } from "../../../../sdks/briefcase-job.sdk";
import { FactSdk } from '@sdks/fact.sdk';

export class DynamicChoiceRepository {
    private autoRedactionJobs: Enumeration[];
    private autoRedactionJobsLoaded = false;

    private custodians: Enumeration[];
    private custodiansLoaded = false;

    private uploaders: Enumeration[];
    private uploadersLoaded = false;

    private batches: Enumeration[];
    private batchesLoaded = false;

    private briefcases: Enumeration[];
    private briefcasesLoaded = false;

    private reviewSets: Enumeration[];
    private reviewSetsLoaded = false;

    private prodJobs: Enumeration[];
    private prodJobsLoaded = false;

    private docSources: Enumeration[];
    private docSourcesLoaded = false;

    private reviewers: Enumeration[];
    private reviewersLoaded = false;

    private docTypes: Enumeration[];
	private docTypesLoaded = false;

    private facts: Enumeration[];
    private factsLoaded = false;

    constructor(
        private autoRedactionService: AutoRedactionService,
        private custodianService: CustodianService,
		private userManager: UserManager,
		private uploadBatchSdk: UploadBatchesSdk,
        private reviewSetService: ReviewSetService,
        private productionJobSdk: ProductionJobSdk,
        private docService: DocService,
		private briefcaseJobSdk: BriefcaseJobSdk,
        private factSdk: FactSdk
    ) { }

    loadChoicesFor(field: FilterField): void {
        switch (field.fieldId) {
            case FilterFieldGuids.AutoRedactionRuleId:
                this.autoRedactionService.getAutoRedactionJobs().subscribe(jobs => {
                    const retList = [];
                    jobs.forEach((j) =>{
                        if (j.runStatus === RunStatus.completed)
                            j.autoRedactionRules.forEach((r) => {
                                retList.push({
                                    value: r.id,
                                    displayName: `${j.title}: ${r.ruleTitle}`
                                });
                            });
                    });
                    this.autoRedactionJobs = <Enumeration[]>retList;
                    this.autoRedactionJobsLoaded = true;
                });
                break;
            case BuiltInFieldId.Custodian:
				this.custodianService.getCustodians().subscribe(custodians => {
                    this.custodians = custodians.map(c => <Enumeration>{ value: c.custodianId, displayName: c.name });
                    this.custodiansLoaded = true;
                });
                break;
            case BuiltInFieldId.CreatedBy:
                this.userManager.getUploaders().subscribe(uploaders => {
                    this.uploaders = uploaders.map(u => <Enumeration>{ value: u.id, displayName: u.userName });
                    this.uploadersLoaded = true;
                });
                break;
            case BuiltInFieldId.UploadBatchId:
				this.uploadBatchSdk.get({skip: null, take: null, orderByColumn: 'batchTitle', orderByDirection: 'asc'}).subscribe(batches => {
					this.batches = batches.items.map(b => <Enumeration>{ value: b.id, displayName: b.title });
					this.batchesLoaded = true;
				});
                break;
            case FilterFieldGuids.BriefcaseJobId:
                this.briefcaseJobSdk.get().subscribe(briefcases => {
                    this.briefcases = briefcases.items.map(b => <Enumeration>{ value: b.id, displayName: b.title });
                    this.briefcasesLoaded = true;
                });
                break;
            case FilterFieldGuids.ReviewSetId:
                this.reviewSetService.getReviewSets().subscribe(sets => {
                    this.reviewSets = sets.map(s => <Enumeration>{ value: s.reviewSetId, displayName: s.title });
                    this.reviewSetsLoaded = true;
                });
                break;
            case FilterFieldGuids.ProductionJobId:
                this.productionJobSdk.get().subscribe(jobs => {
                    this.prodJobs = jobs.map(j => <Enumeration>{ value: j.productionJobId, displayName: j.title });
                    this.prodJobsLoaded = true;
                });
                break;
            case BuiltInFieldId.DocSource:
                this.docService.getDistinctDocSources().subscribe(data => {
                    this.docSources = [];

                    data.forEach(ds => {
                        if (ds === '')
							this.docSources.push({ value: SelectFilterComponent.blank, displayName: SelectFilterComponent.blank });
                        else this.docSources.push({ value: ds, displayName: ds });
                    });

                    this.docSourcesLoaded = true;
                });
                break;
            case FilterFieldGuids.ReviewedBy:
				this.userManager.getReviewers().subscribe(data => {
                    this.reviewers = data.map(r => <Enumeration>{ value: r.id, displayName: r.userName });
                    this.reviewersLoaded = true;
                });
                break;
            case BuiltInFieldId.DocType:
                this.docService.getDistinctDocTypes().subscribe(data => {
                    this.docTypes = [];

                    data.forEach(dt => {
                        if (dt === '')
							this.docTypes.push({ value: SelectFilterComponent.blank, displayName: SelectFilterComponent.blank });
                        else this.docTypes.push({ value: dt, displayName: dt });
                    });

                    this.docTypesLoaded = true;
                });
                break;
            case FilterFieldGuids.DocsInFact:
                this.factSdk.getFactFilterChoices().subscribe(data => {
                    this.facts = data.map(r => <Enumeration>{ value: r.value, displayName: r.displayName });
                    this.factsLoaded = true;
                });
                break;
            default:
                console.warn(`Unrecognized filter field: ${field.fieldId}. Choices not loaded.`);
        }
    }

    findChoicesFor(field: FilterField): Enumeration[] {
        switch (field.fieldId) {
            case FilterFieldGuids.AutoRedactionRuleId:
                return this.autoRedactionJobs;
            case BuiltInFieldId.Custodian:
                return this.custodians;
            case BuiltInFieldId.CreatedBy:
                return this.uploaders;
            case BuiltInFieldId.UploadBatchId:
                return this.batches;
            case FilterFieldGuids.BriefcaseJobId:
                return this.briefcases;
            case FilterFieldGuids.ReviewSetId:
                return this.reviewSets;
            case FilterFieldGuids.ProductionJobId:
                return this.prodJobs;
            case BuiltInFieldId.DocSource:
                return this.docSources;
            case FilterFieldGuids.ReviewedBy:
                return this.reviewers;
            case BuiltInFieldId.DocType:
                return this.docTypes;
            case FilterFieldGuids.DocsInFact:
                return this.facts;
            default:
                console.warn(`Unrecognized filter field: ${field.fieldId}. Choices not loaded.`);
        }
    }

    findLoadStatusFor(field: FilterField): boolean {
        switch (field.fieldId) {
            case FilterFieldGuids.AutoRedactionRuleId:
                return this.autoRedactionJobsLoaded;
            case BuiltInFieldId.Custodian:
                return this.custodiansLoaded;
            case BuiltInFieldId.CreatedBy:
                return this.uploadersLoaded;
            case BuiltInFieldId.UploadBatchId:
                return this.batchesLoaded;
            case FilterFieldGuids.BriefcaseJobId:
                return this.briefcasesLoaded;
            case FilterFieldGuids.ReviewSetId:
                return this.reviewSetsLoaded;
            case FilterFieldGuids.ProductionJobId:
                return this.prodJobsLoaded;
            case BuiltInFieldId.DocSource:
                return this.docSourcesLoaded;
            case FilterFieldGuids.ReviewedBy:
                return this.reviewersLoaded;
            case BuiltInFieldId.DocType:
				return this.docTypesLoaded;
            case FilterFieldGuids.DocsInFact:
                return this.factsLoaded;
            default:
                console.warn(`Unrecognized filter field: ${field.fieldId}. Choices not loaded.`);
        }
    }
}
